import apiService from './api'

export interface DashboardStats {
  totalCourses: number
  activeCourses: number
  totalBookings: number
  pendingBookings: number
  approvedBookings: number
  monthlyRevenue: number
  averageRating: number
  totalReviews: number
}

export interface RecentActivity {
  id: string
  type: 'booking' | 'course' | 'review'
  title: string
  description: string
  timestamp: string
  status?: string
}

class DashboardService {
  /**
   * Get dashboard statistics for the school
   */
  async getDashboardStats(): Promise<{ success: boolean; data: DashboardStats }> {
    try {
      const response = await apiService.getDashboardStats()
      return response
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
      throw error
    }
  }

  /**
   * Get school profile information
   */
  async getSchoolProfile(): Promise<{ success: boolean; data: any }> {
    try {
      const response = await apiService.getSchoolProfile()
      return response
    } catch (error) {
      console.error('Error fetching school profile:', error)
      throw error
    }
  }

  /**
   * Get recent activities for the dashboard
   */
  async getRecentActivities(): Promise<{ success: boolean; data: RecentActivity[] }> {
    try {
      // This would typically come from a dedicated endpoint
      // For now, we'll combine data from different sources
      const [bookingsResponse, coursesResponse] = await Promise.all([
        apiService.post('/bookings/search', { limit: 5, sort: 'submittedAt', order: 'desc' }),
        apiService.get('/courses/my-school?limit=5&sort=updatedAt&order=desc')
      ])

      const activities: RecentActivity[] = []

      // Add recent bookings
      if (bookingsResponse.success && bookingsResponse.data?.bookings) {
        bookingsResponse.data.bookings.forEach((booking: any) => {
          activities.push({
            id: booking._id,
            type: 'booking',
            title: `New booking from ${booking.name}`,
            description: `Booked for ${booking.selectedMonth || 'course'}`,
            timestamp: booking.createdAt,
            status: booking.status
          })
        })
      }

      // Add recent course updates
      if (coursesResponse.success && coursesResponse.data?.courses) {
        coursesResponse.data.courses.forEach((course: any) => {
          activities.push({
            id: course._id,
            type: 'course',
            title: `Course updated: ${course.name}`,
            description: course.description?.substring(0, 100) + '...',
            timestamp: course.updatedAt
          })
        })
      }

      // Sort by timestamp (most recent first)
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

      return {
        success: true,
        data: activities.slice(0, 10) // Return only the 10 most recent
      }
    } catch (error) {
      console.error('Error fetching recent activities:', error)
      throw error
    }
  }

  /**
   * Get school analytics data
   */
  async getAnalytics(): Promise<{ success: boolean; data: any }> {
    try {
      // This would typically come from an analytics endpoint
      // For now, we'll return mock data structure
      const analyticsData = {
        monthlyBookings: [
          { month: 'Jan', count: 12 },
          { month: 'Feb', count: 18 },
          { month: 'Mar', count: 25 },
          { month: 'Apr', count: 22 },
          { month: 'May', count: 30 },
          { month: 'Jun', count: 28 }
        ],
        revenueData: [
          { month: 'Jan', revenue: 12000 },
          { month: 'Feb', revenue: 18000 },
          { month: 'Mar', revenue: 25000 },
          { month: 'Apr', revenue: 22000 },
          { month: 'May', revenue: 30000 },
          { month: 'Jun', revenue: 28000 }
        ],
        coursePerformance: [
          { courseName: '200-Hour YTT', bookings: 45, completionRate: 92 },
          { courseName: '300-Hour YTT', bookings: 28, completionRate: 88 },
          { courseName: 'Yoga Retreat', bookings: 35, completionRate: 95 }
        ]
      }

      return {
        success: true,
        data: analyticsData
      }
    } catch (error) {
      console.error('Error fetching analytics:', error)
      throw error
    }
  }
}

export default new DashboardService()
